import React, { FC, useEffect } from "react"
import L10n from "lib/carrot-l10n"
import { useDispatch, useSelector } from "react-redux"
import { FormProvider, useForm } from "react-hook-form"
import {
  BenefitEnrollmentFlowModalBackButton,
  BenefitEnrollmentModalContinueButton,
  BenefitEnrollmentModalFormSelect
} from "../BenefitEnrollmentFlowModal"
import { Form } from "../../../atoms/forms/Form"
import { Spacer } from "../../../atoms/Atoms"
import { updateUserInfo } from "../../../../../actions/userActions"
import { getAvailableTradingPartners } from "../../../../../reducers/reimbursementDetails"
import { SelectOption } from "../../../../../content/SelectOption"
import { CompleteFormInput } from "../../../atoms/forms/FormInput"
import { getMemberId, getSelectedTradingPartnerPk } from "../../../../../reducers/userInfo"
import { Steps } from "../Workflow/Steps"
import useSubmitWithErrorCatch from "../hooks/useSubmitWithErrorCatch"
import { useProgressTracker } from "../../ProgressTracker"
import { useToggle } from "../../../../../utils/Hooks"
import { useStateMachine } from "../../../../context/stateMachine/StateMachineContext"
import { H2, H4 } from "@carrotfertility/carotene"

const l = new L10n({ locale: "en-US" }).getBoundLocalize()

const Required = "Required"
const FieldNameTradingPartner = "tradingPartnerValue"
const FieldNameMemberId = "memberIdValue"
const ViewName = Steps.PROVIDER_CONNECT
const providerNotListedValue = "OTHER"

const ProviderConnectYourInsuranceView: FC = () => {
  const { setProgress } = useProgressTracker()
  const dispatch = useDispatch()
  const { setNextStep } = useStateMachine(ViewName, null)
  const formMethods = useForm()
  const availableTradingPartners = useSelector(getAvailableTradingPartners)
  const tradingPartnerPk = useSelector(getSelectedTradingPartnerPk)
  const memberId = useSelector(getMemberId)
  const [providerListed, setProviderListed] = useToggle(true)
  const { onSubmit } = useSubmitWithErrorCatch(async (args) => {
    const tradingPartnerPkVal = args[FieldNameTradingPartner].id
    const memberIdVal = args[FieldNameMemberId]
    const tradingPartner = availableTradingPartners.find((tp) => tp.tradingPartnerPk === tradingPartnerPkVal)
    await dispatch(
      updateUserInfo({
        selectedTradingPartnerId: tradingPartner.id,
        memberId: memberIdVal,
        selectedTradingPartnerPk: tradingPartnerPkVal
      })
    )

    setNextStep(ViewName, tradingPartner.id === providerNotListedValue ? providerNotListedValue : "PROVIDER")
  })

  setProgress(2, "CONNECT YOUR INSURANCE")

  const { register, handleSubmit, errors, setValue } = formMethods
  useEffect(() => {
    setValue(FieldNameMemberId, memberId)
    checkIfProviderIsListed({ id: tradingPartnerPk })
  }, [])

  type TradingPartner = {
    id: string
    description: string
    tradingPartnerPk: number
  }

  if (!availableTradingPartners.some((tp) => tp.id === providerNotListedValue)) {
    availableTradingPartners.push({
      id: providerNotListedValue,
      description: l`Provider not listed`,
      tradingPartnerPk: -1
    })
  }

  const options: SelectOption[] = availableTradingPartners.map((tradingPartner: TradingPartner, index) => ({
    key: index,
    label: tradingPartner.description,
    id: tradingPartner.tradingPartnerPk
  }))

  const checkIfProviderIsListed = (selectedValue) => {
    selectedValue.id === -1 ? setProviderListed(false) : setProviderListed(true)
    return selectedValue
  }

  return (
    <FormProvider {...formMethods}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <BenefitEnrollmentFlowModalBackButton />
        <Spacer height={3} />
        <H2 variant="dark">{l`Connect to your health insurance provider`}</H2>
        <Spacer height={2} />
        <H4 variant="dark">{l`PROVIDER NAME`}</H4>
        <Spacer height={0.5} />

        <BenefitEnrollmentModalFormSelect
          name={FieldNameTradingPartner}
          defaultValue={tradingPartnerPk}
          options={options}
          getSelectedValue={checkIfProviderIsListed}
          onChange={checkIfProviderIsListed}
        />

        {providerListed ? (
          <>
            <H4 variant="dark">{l`MEMBER ID`}</H4>
            <Spacer height={0.5} />
            <CompleteFormInput name={"memberIdValue"} errors={errors} ref={register({ required: Required })} />
          </>
        ) : (
          <Spacer height={4} />
        )}
        <Spacer height={2} />
        <BenefitEnrollmentModalContinueButton onClick={handleSubmit(onSubmit)} />
      </Form>
    </FormProvider>
  )
}

export { ProviderConnectYourInsuranceView }
